import { removeArrayItem, updateArrayItem } from '@/util/array';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { mapErrorsToInputs } from '@/util/forms';
import locationsService from '@/api/locations-service';

const getDefaultFormItem = () => ({});

export const initialLocationsFilterParams = () => ({});

const state = {
  locations: null,
  locationsPagination: {},
  newLocation: getDefaultFormItem(),
  editedLocation: {},
  changeLocationsOrder: false,
  snapshotBeforeDrag: null,
  locationValidationErrors: {},
  locationsFilterParams: initialLocationsFilterParams(),
};

const mutations = {
  SET_LOCATIONS(state, { data, meta }) {
    state.locations = data;
    state.locationsPagination = {
      current_page: meta.current_page,
      per_page: meta.per_page,
      total: meta.total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.locationsFilterParams = params;
  },

  STORE_LOCATION(state, location) {
    state.locations = [...state.locations, location];
    state.newLocation = getDefaultFormItem();
    state.locationValidationErrors = {};
    state.locationsPagination.total += 1;
  },

  RESET_FORM_STATE(state) {
    state.newLocation = getDefaultFormItem();
    state.locationValidationErrors = {};
  },

  SET_EDITED_LOCATION(state, location) {
    state.locationValidationErrors = {};
    state.editedLocation = JSON.parse(JSON.stringify(location));
  },

  UPDATE_LOCATION(state, location) {
    state.locations = updateArrayItem(state.locations, location);
  },

  DELETE_LOCATION(state, location) {
    state.locations = removeArrayItem(state.locations, location);
    state.locationsPagination.total -= 1;
  },

  SET_LOCATION_VALIDATION_ERRORS(state, validationErrors) {
    state.locationValidationErrors = validationErrors;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.locationValidationErrors[field];
  },

  SET_CHANGE_LOCATION_ORDER(state, show) {
    state.changeLocationsOrder = show
  },

  SET_DRAG_SNAPSHOT(state, locations) {
    state.snapshotBeforeDrag = locations
  },

  SET_LOCATION_AIRPORT(state, airportId) {
    state.newLocation.airport_id = airportId
  }
};

const actions = {
  fetchLocations({ state, commit }, params) {
    commit('SET_FILTER_PARAMS', params);
    return locationsService.getPage(params).then((res) => {
      commit('SET_LOCATIONS', res.data);
      if (params.page !== state.locationsFilterParams.page) {
        window.scrollTo(0, 0);
      }
    });
  },

  storeLocation({ commit }, { location, addToList }) {
    return locationsService
      .create(location)
      .then((res) => {
        if (addToList) {
          commit('STORE_LOCATION', res.data.data);
        } else {
          commit('RESET_FORM_STATE')
        }
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('location_created'));
        return res;
      })
      .catch((err) => {
        commit('SET_LOCATION_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editLocation({ state, commit }, locationId) {
    const location = state.locations?.find((s) => s.id === locationId);
    if (location) {
      commit('SET_EDITED_LOCATION', location);
      return Promise.resolve(location);
    }
    return locationsService.getById(locationId).then((res) => {
      commit('SET_EDITED_LOCATION', res.data);
      return res.data;
    });
  },

  updateLocation({ commit }, { location, removeFromList }) {
    return locationsService
      .update(location)
      .then((res) => {
        if (removeFromList) {
          commit('DELETE_LOCATION', location)
        } else {
          commit('UPDATE_LOCATION', res.data.data);
        }

        eventBus.$emit(OPEN_SNACKBAR, i18n.t('location_updated'));
      })
      .catch((err) => {
        commit('SET_LOCATION_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  deleteLocation({ commit }, location) {
    openConfirmDialog({
      title: i18n.t('confirm_location_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      locationsService.delete(location).then(() => {
        commit('DELETE_LOCATION', location);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('location_deleted'));
      });
    });
  },

  updateOrder({ commit }, payload) {
    return locationsService
      .changeOrder(payload)
      .then((res) => {
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('position_updated'))
      })
      .catch((err) => {
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('unexpected_error'))
      })
  },

  updateNumber({ commit }, location) {
    return locationsService
      .changeNumber(location)
      .then((res) => {
        commit('UPDATE_LOCATION', res.data.data)
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('security_seal_number_updated'));
      })
      .catch((err) => {
        commit('SET_LOCATION_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  generatePdfList({ commit }, params) {
    return locationsService
      .generatePdfList(params)
      .catch((res) => {
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('failed_to_generate_pdf'));
      })
  },

  clearLocationValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },

  toggleChangeLocationOrder({ commit }, show) {
    commit('SET_CHANGE_LOCATION_ORDER', show)
  },

  setDragSnapshot({ commit }, locations) {
    commit('SET_DRAG_SNAPSHOT', locations)
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
